import { useContext, useState } from 'react';
import { useSnackbar } from 'notistack';
import { DateTime } from 'luxon';
import { CouponPackMasterFormInput } from '../../../../types/couponPackMaster';
import { fromInputDate, fromInputDateAndTime, validateStartEnd } from '../../../../util';
import { OrganizationContext, OrganizationContextProps } from '../../../../components/contexts/organization';

type InValid = {
  invalid: true;
};

type ValidDateTime = {
  invalid: false;
  displayStartDateTime: DateTime;
  displayEndDateTime: DateTime;
  validityStartDateTime: DateTime | undefined;
  validityEndDateTime: DateTime;
};

export type FormCouponPackMasterUseCase = {
  organization?: OrganizationContext;
  organizationLoading: boolean;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  validateDateAndGetDateTime: (input: CouponPackMasterFormInput) => InValid | ValidDateTime;
  orgContext: OrganizationContextProps;
};

export const useFormCouponPackMaster = (): FormCouponPackMasterUseCase => {
  const orgContext = useContext(OrganizationContext);
  const organization = orgContext.organization;
  const organizationLoading = orgContext.loading;
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);

  const validateDateAndGetDateTime = (input: CouponPackMasterFormInput): InValid | ValidDateTime => {
    const displayStartDateTime = fromInputDate(input.displayStartDate);
    const displayEndDateTime = fromInputDate(input.displayEndDate);

    const validityStartDateTime = fromInputDateAndTime(input.validityStartDate, input.validityStartTime);
    const validityEndDateTime = fromInputDateAndTime(input.validityEndDate, input.validityEndTime);

    if (!!input.validityStartDate !== !!input.validityStartTime) {
      const key = enqueueSnackbar('日付と時刻はどちらも入力してください。', {
        variant: 'warning',
        persist: true,
        onClick: () => closeSnackbar(key),
      });
      return { invalid: true };
    }

    if (
      !displayStartDateTime ||
      !displayEndDateTime ||
      !validityEndDateTime ||
      !displayStartDateTime.isValid ||
      !displayEndDateTime.isValid ||
      (validityStartDateTime && !validityStartDateTime.isValid) ||
      !validityEndDateTime.isValid
    ) {
      const key = enqueueSnackbar('表示期間、有効期限、取得期限の日時情報を正しく入力してください', {
        variant: 'warning',
        persist: true,
        onClick: () => closeSnackbar(key),
      });
      return { invalid: true };
    }

    if (
      !displayStartDateTime ||
      !displayEndDateTime ||
      !validityEndDateTime ||
      !validateStartEnd(displayStartDateTime, displayEndDateTime) ||
      !validateStartEnd(validityStartDateTime, validityEndDateTime)
    ) {
      const key = enqueueSnackbar('開始日時が前になるように修正してください', {
        variant: 'warning',
        persist: true,
        onClick: () => closeSnackbar(key),
      });
      return { invalid: true };
    }

    return {
      invalid: false,
      displayStartDateTime,
      displayEndDateTime,
      validityStartDateTime,
      validityEndDateTime,
    };
  };

  return {
    organization,
    organizationLoading,
    loading,
    setLoading,
    validateDateAndGetDateTime,
    orgContext,
  };
};
